import { QueryRef } from 'apollo-angular'
import { Injectable } from '@angular/core'
import { Apollo } from 'apollo-angular'
import { Observable } from 'rxjs'
import { DocumentNode } from 'graphql'
import gql from 'graphql-tag'
import { ApolloQueryResult } from 'apollo-client'
import { SubscriptionState } from '../../types'
import { DeliveryMethod } from 'src/modules/delivery-options/enums'

export interface IAccount {
  id: number
  enabledFeatures: ('deadlines' | 'content_types' | 'direct_assignments')[]
  lastFour: string
  billingType: 'credit_card' | 'paypal'
  cardType: string
  renewToMultipass: boolean
}

export interface IWriter {
  id: number
  displayName: string
  shortDisplayName: string
  slug: string
  state: string
  avatar: string
  section?: 'client' | 'account' | 'pool'
  userFraudCount: number
}

export interface IContentType {
  id: number
  code: string
  name: string
  wordMin: number
  wordMax: number
}

export interface IKeyword {
  id: number
  kind: 'open' | 'deadline' | 'assigned' | 'proposed'
  customerId: number
  phrase: string
  description: string
  state: string
  priority: string
  postedCount: number
  queuedCount: number
  postsCount: number
  awaitingEditingCount: number
  newCount: number
  unaddressedCount: number
  deadline: Date
  timeZone: string
  writer: IWriter
  writerId: number
  previousWriter: IWriter
  postLimit: number
  focusKeywords: string
  link: string
  linkText: string
  externalLinks: boolean
  searcherIntent: string
  seoNotes: string
  updatedAt: Date
  firstDraftStartedAt: Date
  createdAt: Date
  activatedAt: Date
  documents: {
    id: number | string
    name: string
    fileUrl: string
    pending: boolean // client
  }[]

  contentType: IContentType
  expanded: boolean // client
  editing: boolean // client
  rewrite: boolean
  allowInsecureLinks: boolean
  requireSameTitle: boolean
  quantityFromCredits: number
  reservedPostCreditsCount: number
  product: IProduct
  multipassCreditReserved: number
  alreadyPurchasedPosts: number
}

export interface IExtra {
  id: number
  code: string
  active: boolean
}

export interface SourceContent {
  id?: number
  type: 'hubspot' | 'copypaste' | 'url'
  externalIdentifier: string
  content: string
}

export interface Request {
  id: number
  type: 'VideoRequest'
  videoType: string
  accentTextColor: string
  font: string
  videoTitle: string
  musicGuidance: string
  logo: string
  otherPreferences: string
  additionalResources: string
  state: 'draft' | 'active' | 'fulfilled' | 'deleted'
  sourceContent: SourceContent
}

export interface ICustomer {
  id: number
  multipass: boolean
  balance: number
  businessName: string
  activeKeywordsCount: number
  assignedKeywordsCount: number
  activeRequestsCount: number
  postCreditsCount: number
  postDeliveryMethod: DeliveryMethod
  deadlineBufferHours: number
  state: SubscriptionState
  keywords: {
    entries: IKeyword[]
    totalCount: number
  }
  requests: {
    entries: Request[]
    totalCount: number
  }
  plan: {
    id: number
    code: string
    words: number
    extraPostPrice: number
  }
  subscription: {
    id: number
    extraPostPrice: number
    quantity: number
    pastDueAt: Date
    productsAtActivation?: String[]
  }
  extras: IExtra[]
  hubspotPosts: {
    id: number
    blogId: number
    title: string
    postBody: string
    postSummary: string
  }[]
  hubspotCredential: {
    id: number
  }
  preferredWriters: IWriter[]
  preferredByAccount: IWriter[]
  preferredByCustomer: IWriter[]
  pooledWriters: IWriter[]
  blockedWriters: IWriter[]
  subscriptionPriceList: IPriceList
  submissionMode: string
}

export interface IPurchase {
  purchasedAt: Date
  extra: {
    code: string
  }
}

export interface IUser {
  id: number
  adminImpersonating: boolean
}

export type RequestActivationMethod = 'credit' | 'charge'

export interface Data {
  currentUser: IUser
  customer: ICustomer
  account: IAccount
  contentTypes: IContentType[]
}

export interface AmFont {
  id: string
  family: string
  preview: string
}

export interface IAddOn {
  id: number
  code: string
  name: string
  words: number
  valueInCredits: number
  addOnId: number
  quantity: number
}

export interface IProduct {
  id: number
  type: string
  code: string
  name: string
  words: number
  valueInCredits: number
  standard: boolean
  addOns: IAddOn[]
}

export interface IPriceList {
  id: number
  name: string
  creditPriceInCents: number
  aLaCarteCreditPriceInCents: number
  products: IProduct[]
}

export type EditKeywordPageData = {
  customer: {
    id: number
    multipass: boolean
    keyword: IKeyword
    plan: {
      code: string
      words: number
    }
    subscriptionPriceList: IPriceList
  }
  account: IAccount
  currentUser: {
    id: number
    uiPreferences: {
      reminderBetterSubmissions: boolean
    }
  }
  contentTypes: IContentType[]
}

export type EditRequestPageData = {
  customer: ICustomer & {
    request: Request
  }
}

export type DuplicateRequestPageData = {
  customer: {
    id: number
    request: Request
  }
}

export type DuplicateKeywordPageData = {
  customer: {
    id: number
    keyword: IKeyword
  }
}

export type KeywordLayoutData = {
  account: {
    id: number
    tenTopicsPrice: number
    tenTopicPriceDefault: number
  }
  customer: CustomerExtraPurchases
}

export type CustomerExtraPurchases = {
  id: number
  state: SubscriptionState
  plan: {
    id: number
    code: string
  }
  extraPurchases: IPurchase[]
}

export type NewKeywordPageData = {
  customer: ICustomer
  account: IAccount
  currentUser: {
    id: number
    uiPreferences: {
      reminderBetterSubmissions: boolean
    }
  }
  contentTypes: IContentType[]
}

export const fragments: {
  user: DocumentNode
  account: DocumentNode
  customer: DocumentNode
  keyword: DocumentNode
  contentTypes: DocumentNode
  subscriptionPriceList: DocumentNode
} = {
  user: gql`
    fragment KeywordsPageUser on User {
      id
      adminImpersonating
    }
  `,
  account: gql`
    fragment KeywordsPageAccount on Account {
      id
      enabledFeatures
      lastFour
      billingType
      cardType
      renewToMultipass
    }
  `,
  customer: gql`
    fragment KeywordsPageCustomer on Customer {
      id
      multipass
      businessName
      activeKeywordsCount
      assignedKeywordsCount
      activeRequestsCount
      postCreditsCount
      totalKeywordsPieces
      deadlineBufferHours
      state
      balance
      submissionMode
      plan {
        id
        code
        words
        extraPostPrice
      }
      subscription {
        id
        pastDueAt
        ... on LegacySubscription {
          quantity
          extraPostPrice
        }
        ... on MultipassSubscription {
          productsAtActivation
        }
      }
      extraPurchases {
        id
        extra {
          id
          code
        }
        purchasedAt
      }
      hubspotCredential {
        id
      }
      preferredWriters {
        id
        displayName
        state
        avatar
        userFraudCount
      }
      preferredByCustomer {
        id
        displayName
        state
        avatar
        userFraudCount
      }
      preferredByAccount {
        id
        displayName
        state
        avatar
        userFraudCount
      }
      pooledWriters {
        id
        displayName
        state
        avatar
        userFraudCount
      }
      blockedWriters {
        id
        displayName
        state
        avatar
        userFraudCount
      }
    }
  `,
  keyword: gql`
    fragment KeywordsPageKeyword on Keyword {
      id
      kind
      phrase
      description
      state
      priority
      customerId
      postedCount
      queuedCount
      postsCount
      awaitingEditingCount
      newCount
      unaddressedCount
      deadline
      timeZone
      postLimit
      focusKeywords
      link
      linkText
      externalLinks
      searcherIntent
      seoNotes
      updatedAt
      firstDraftStartedAt
      createdAt
      activatedAt
      rewrite
      allowInsecureLinks
      requireSameTitle
      quantityFromCredits
      reservedPostCreditsCount
      contentType {
        id
        name
      }
      writerId
      writer {
        id
        shortDisplayName
        displayName
        slug
        avatar
      }
      previousWriter {
        id
        shortDisplayName
        displayName
        slug
        avatar
      }
      documents {
        id
        name
        fileUrl
        path
        pending @client
      }
      product {
        id
        code
        name
        words
        quantity
        addOns {
          id
          code
          name
          quantity
        }
      }
      multipassCreditReserved
      alreadyPurchasedPosts
    }
  `,
  contentTypes: gql`
    fragment KeywordsPageContentTypes on ContentType {
      id
      code
      name
      wordMin
      wordMax
    }
  `,
  subscriptionPriceList: gql`
    fragment SubscriptionPriceList on Customer {
      subscriptionPriceList {
        id
        aLaCarteCreditPriceInCents
        products {
          id
          code
          name
          valueInCredits
          standard
          words
          addOns {
            id
            code
            name
            valueInCredits
            addOnId
          }
        }
      }
    }
  `,
}

export const KeywordsPageQuery: DocumentNode = gql`
  query KeywordsPageQuery(
    $customerId: ID!
    $states: [String]
    $search: String
    $page: Int
    $pageSize: Int
    $order: String
  ) {
    currentUser {
      ...KeywordsPageUser
    }
    account {
      ...KeywordsPageAccount
    }
    customer(id: $customerId) {
      ...KeywordsPageCustomer
      keywords(page: $page, perPage: $pageSize, state: $states, search: $search, order: $order) {
        entries {
          ...KeywordsPageKeyword
        }
        totalCount
      }
      requests(page: $page, perPage: $pageSize, searchByTitle: $search) {
        entries {
          id
          state
          videoType
          logo
          additionalResources
          videoTitle
          accentTextColor
          font
          otherPreferences
          type
          sourceContent {
            id
            type
            externalIdentifier
            content
          }
        }
        totalCount
      }
      hubspotPosts {
        id
        title
      }
    }
    contentTypes {
      ...KeywordsPageContentTypes
    }
  }
  ${fragments.user}
  ${fragments.account}
  ${fragments.customer}
  ${fragments.keyword}
  ${fragments.contentTypes}
`

export const KeywordsLayoutQuery: DocumentNode = gql`
  query KeywordsPageQuery($customerId: ID!) {
    customer(id: $customerId) {
      id
      state
      plan {
        id
        code
      }
      extraPurchases {
        id
        extra {
          id
          code
        }
        purchasedAt
      }
    }
    account {
      id
      tenTopicsPrice
      tenTopicPriceDefault
    }
  }
`

export const EditKeywordPageQuery: DocumentNode = gql`
  query EditKeywordPageQuery($customerId: ID!, $keywordId: ID!, $multipass: Boolean!) {
    customer(id: $customerId) {
      ...KeywordsPageCustomer
      keyword(id: $keywordId) {
        ...KeywordsPageKeyword
      }
      ...SubscriptionPriceList @include(if: $multipass)
    }
    account {
      ...KeywordsPageAccount
    }
    currentUser {
      id
      uiPreferences {
        reminderBetterSubmissions
      }
    }
    contentTypes {
      ...KeywordsPageContentTypes
    }
  }
  ${fragments.keyword}
  ${fragments.account}
  ${fragments.contentTypes}
  ${fragments.customer}
  ${fragments.subscriptionPriceList}
`

export const EditRequestPageQuery: DocumentNode = gql`
  query EditRequestPageQuery($customerId: ID!, $requestId: ID!) {
    customer(id: $customerId) {
      ...KeywordsPageCustomer
      request(id: $requestId) {
        id
        type
        videoTitle
        videoType
        accentTextColor
        font
        logo
        otherPreferences
        additionalResources
        state
        sourceContent {
          id
          type
          externalIdentifier
          content
        }
      }
    }
  }
  ${fragments.customer}
`

export const DuplicateKeywordPageQuery: DocumentNode = gql`
  query DuplicateKeywordPageQuery($customerId: ID!, $keywordId: ID!) {
    customer(id: $customerId) {
      id
      keyword(id: $keywordId) {
        ...KeywordsPageKeyword
      }
    }
  }
  ${fragments.keyword}
`

export const DuplicateRequestPageQuery: DocumentNode = gql`
  query DuplicateRequestPageQuery($customerId: ID!, $requestId: ID!) {
    customer(id: $customerId) {
      id
      request(id: $requestId) {
        id
        type
        videoTitle
        videoType
        accentTextColor
        font
        logo
        otherPreferences
        additionalResources
        state
        sourceContent {
          id
          type
          externalIdentifier
          content
        }
      }
    }
  }
`

export const NewKeywordPageQuery: DocumentNode = gql`
  query NewKeywordPageQuery($customerId: ID!, $contentSearch: String, $multipass: Boolean!) {
    customer(id: $customerId) {
      balance
      postDeliveryMethod
      ...KeywordsPageCustomer
      hubspotPosts(search: $contentSearch) {
        id
        title
        postBody
        postSummary
      }
      ...SubscriptionPriceList @include(if: $multipass)
    }
    account {
      ...KeywordsPageAccount
    }
    currentUser {
      id
      uiPreferences {
        reminderBetterSubmissions
      }
    }
    contentTypes {
      ...KeywordsPageContentTypes
    }
  }
  ${fragments.account}
  ${fragments.customer}
  ${fragments.contentTypes}
  ${fragments.subscriptionPriceList}
`

export const FontListQuery: DocumentNode = gql`
  query FontListQuery {
    amFontList {
      id
      family
      preview
    }
  }
`

@Injectable()
export class KeywordQueries {
  constructor(private apollo: Apollo) {}

  mainQuery(
    customerId: number,
    states: string[] = ['active'],
    page: number,
    pageSize: number,
    order: string
  ): Observable<ApolloQueryResult<Data>> {
    return this.apollo.watchQuery<Data>({
      query: KeywordsPageQuery,
      variables: {
        customerId,
        states,
        search: null,
        page,
        pageSize,
        order,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }

  extrasQuery(customerId: number): Observable<ApolloQueryResult<KeywordLayoutData>> {
    return this.apollo.watchQuery<KeywordLayoutData>({
      query: KeywordsLayoutQuery,
      variables: { customerId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }

  searchQuery(
    customerId: number,
    filters: {
      states: string[]
      search: string
    } = {
      states: ['active', 'fulfilled', 'paused', 'deleted'],
      search: '',
    },
    page,
    pageSize,
    order: string
  ): Observable<ApolloQueryResult<Data>> {
    return this.apollo.watchQuery<Data>({
      query: KeywordsPageQuery,
      variables: {
        customerId,
        states: filters.states,
        search: filters.search,
        page,
        pageSize,
        order,
      },
    }).valueChanges
  }

  editKeywordPageQuery(
    customerId: number,
    keywordId: number,
    multipass: boolean = false
  ): Observable<ApolloQueryResult<EditKeywordPageData>> {
    return this.apollo.watchQuery<EditKeywordPageData>({
      query: EditKeywordPageQuery,
      variables: { customerId, keywordId, multipass },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }

  editRequestPageQuery(
    customerId: number,
    requestId: number
  ): Observable<ApolloQueryResult<EditRequestPageData>> {
    return this.apollo.watchQuery<EditRequestPageData>({
      query: EditRequestPageQuery,
      variables: { customerId, requestId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }

  duplicateKeywordPageQuery(
    customerId: number,
    keywordId: number
  ): Observable<ApolloQueryResult<DuplicateKeywordPageData>> {
    return this.apollo.watchQuery<DuplicateKeywordPageData>({
      query: DuplicateKeywordPageQuery,
      variables: { customerId, keywordId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }

  duplicateRequestPageQuery(
    customerId: number,
    requestId: number
  ): Observable<ApolloQueryResult<DuplicateRequestPageData>> {
    return this.apollo.watchQuery<DuplicateRequestPageData>({
      query: DuplicateRequestPageQuery,
      variables: { customerId, requestId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }

  newKeywordPageQuery(
    customerId: number,
    contentSearch: string = '',
    multipass: boolean = false
  ): QueryRef<NewKeywordPageData> {
    return this.apollo.watchQuery<NewKeywordPageData>({
      query: NewKeywordPageQuery,
      variables: { customerId, contentSearch, multipass },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    })
  }

  fontListQuery(): Observable<ApolloQueryResult<{ amFontList: AmFont[] }>> {
    return this.apollo.watchQuery<{ amFontList: AmFont[] }>({
      query: FontListQuery,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }).valueChanges
  }
}
